// Agreement Rate Selectors

import { createSelector } from "@reduxjs/toolkit";
import { RootState } from "app/store";
import AutomatedScoringType from "enums/AutomatedScoringType";
import FeatureToggle from "enums/FeatureToggle";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import {
    selectIsAutomatedEvaluationForm,
    selectForm,
    selectQuestion,
} from "./qualityFormSlices";
import { AgreementRateData } from "./qualityFormSlices/qualityFormStates";
import { selectHasQualityEvaluationSearchAny } from "features/auth/currentUserSlice";

/**
 * Selects the form-level agreement rate data
 */
export const selectFormAgreementRate = (
    state: RootState
): AgreementRateData | undefined => {
    return state.qualityForm.data.form?.agreementRate?.formAgreementRate;
};

/**
 * Selects the loading state for form-level agreement rate
 */
export const selectFormAgreementRateLoading = (state: RootState): boolean => {
    return state.qualityForm.data.form?.agreementRate?.loading ?? false;
};

/**
 * Selects whether the form has agreement rate data available
 */
export const selectHasFormAgreementRateData = (state: RootState): boolean => {
    const agreementRateData = selectFormAgreementRate(state);
    return agreementRateData?.hasData ?? false;
};

/**
 * Selects the error state for form-level agreement rate
 */
export const selectFormAgreementRateError = (
    state: RootState
): string | undefined => {
    return state.qualityForm.data.form?.agreementRate?.error;
};

/**
 * Selects whether all agreement rate feature dependencies are enabled
 * Requires: WEM-1429 and WEM-556 Reporting Customer.
 */
export const selectIsAgreementRateFeatureEnabled = (
    state: RootState
): boolean => {
    return (
        selectFeatureToggle(
            state,
            FeatureToggle.AUTOCOMPLETE_EVALUATION_AGREEMENT_RATES
        ) &&
        selectFeatureToggle(
            state,
            FeatureToggle.QM_RELEASE_EVALUATION_REPORTING_CUSTOMER
        )
    );
};

/**
 * Selects whether we should attempt to load agreement rate data
 * The thunk will determine if the current form is the latest published version
 */
export const selectShouldLoadAgreementRate = (state: RootState): boolean => {
    const isAgreementRateFeatureEnabled =
        selectIsAgreementRateFeatureEnabled(state);
    const isAutomatedEvaluationForm = selectIsAutomatedEvaluationForm(state);
    const isLoading = selectFormAgreementRateLoading(state);
    // Treat "we already received a successful response" as resolved (e.g. no automated questions → hasData false); prevents re-load loops
    const hasResolved =
        state.qualityForm.data.form?.agreementRate?.formAgreementRate !==
        undefined;
    const hasError = selectFormAgreementRateError(state) !== undefined;
    const isDraftFormAgreementRateFeatureEnabled = selectFeatureToggle(
        state,
        FeatureToggle.DRAFT_FORM_AGREEMENT_RATE
    );
    const hasEvaluationSearchPermission =
        selectHasQualityEvaluationSearchAny(state);
    const currentForm = selectForm(state);

    // Allow loading of agreement rate data for draft forms if WEM-1579 FT is enabled, otherwise published only
    const isPublicationStatusEligible =
        !!currentForm?.published || isDraftFormAgreementRateFeatureEnabled;
    // Allow loading of agreement rate data for regular forms as well if WEM-1579 FT is enabled, otherwise automated only
    const isFormTypeEligible =
        isDraftFormAgreementRateFeatureEnabled || isAutomatedEvaluationForm;

    // Only load if 1) all two FTs (WEM-1429 and WEM-556) are enabled, 2) form type is eligible, 3) form is published or WEM-1579 FT is enabled, and 4) we haven't resolved yet (no successful response), aren't loading, and have no error
    const shouldLoad =
        isAgreementRateFeatureEnabled &&
        isFormTypeEligible &&
        isPublicationStatusEligible &&
        hasEvaluationSearchPermission &&
        !isLoading &&
        !hasResolved &&
        !hasError;

    return shouldLoad;
};

/**
 * Selects whether the form should display agreement rate
 */
export const selectShouldDisplayFormAgreementRate = (
    state: RootState
): boolean => {
    const isAgreementRateFeatureEnabled =
        selectIsAgreementRateFeatureEnabled(state);
    const isAutomatedEvaluationForm = selectIsAutomatedEvaluationForm(state);
    const hasData = selectHasFormAgreementRateData(state);
    const agreementRateData = selectFormAgreementRate(state);
    const currentForm = selectForm(state);
    const isDraftFormAgreementRateFeatureEnabled = selectFeatureToggle(
        state,
        FeatureToggle.DRAFT_FORM_AGREEMENT_RATE
    );

    // Allow displaying agreement rate data for draft forms if WEM-1579 FT is enabled, otherwise published only
    const isPublicationStatusEligible =
        !!currentForm?.published || isDraftFormAgreementRateFeatureEnabled;
    // Allow displaying agreement rate data for regular forms as well if WEM-1579 FT is enabled, otherwise automated only
    const isFormTypeEligible =
        isDraftFormAgreementRateFeatureEnabled || isAutomatedEvaluationForm;

    const hasEvaluationSearchPermission =
        selectHasQualityEvaluationSearchAny(state);

    return (
        isAgreementRateFeatureEnabled &&
        isFormTypeEligible &&
        hasData &&
        !!agreementRateData &&
        isPublicationStatusEligible &&
        hasEvaluationSearchPermission
    );
};

/**
 * Combined selector for agreement rate display state
 */
export const selectFormAgreementRateDisplayState = createSelector(
    [
        selectFormAgreementRate,
        selectFormAgreementRateLoading,
        selectFormAgreementRateError,
        selectShouldDisplayFormAgreementRate,
        selectHasFormAgreementRateData,
    ],
    (agreementRateData, loading, error, shouldDisplay, hasData) => ({
        data: agreementRateData,
        loading,
        error,
        shouldDisplay,
        hasData,
    })
);

/**
 * Selects the agreement rate data for a specific question by question ID
 */
export const selectQuestionAgreementRate = (
    state: RootState,
    questionId: string
): AgreementRateData | undefined => {
    return selectQuestion(state, questionId)?.agreementRate;
};

/**
 * Selects whether a question has agreement rate data available
 */
export const selectHasQuestionAgreementRateData = (
    state: RootState,
    questionId: string
): boolean => {
    const agreementRateData = selectQuestionAgreementRate(state, questionId);
    return agreementRateData?.hasData ?? false;
};

/**
 * Selects whether we should display agreement rate for a specific question
 */
export const selectShouldDisplayQuestionAgreementRate = (
    state: RootState,
    questionId: string
): boolean => {
    const isAgreementRateFeatureEnabled =
        selectIsAgreementRateFeatureEnabled(state);
    const isAutomatedEvaluationForm = selectIsAutomatedEvaluationForm(state);
    const question = state.qualityForm.data.questions?.[questionId];
    const isDraftFormAgreementRateFeatureEnabled = selectFeatureToggle(
        state,
        FeatureToggle.DRAFT_FORM_AGREEMENT_RATE
    );
    // Allow displaying agreement rate data for regular forms as well if WEM-1579 FT is enabled, otherwise automated only
    const isFormTypeEligible =
        isDraftFormAgreementRateFeatureEnabled || isAutomatedEvaluationForm;

    if (!question) {
        return false;
    }

    const hasAiScoringOrEa =
        question.automatedScoringType !== AutomatedScoringType.NO_AUTOMATION;

    return (
        isAgreementRateFeatureEnabled && isFormTypeEligible && hasAiScoringOrEa
    );
};

/**
 * Combined selector for question agreement rate display state
 */
export const selectQuestionAgreementRateDisplayState = createSelector(
    [
        (state: RootState, questionId: string) =>
            selectQuestionAgreementRate(state, questionId),
        (state: RootState, questionId: string) =>
            selectShouldDisplayQuestionAgreementRate(state, questionId),
        (state: RootState, questionId: string) =>
            selectHasQuestionAgreementRateData(state, questionId),
    ],
    (agreementRateData, shouldDisplay, hasData) => ({
        data: agreementRateData,
        shouldDisplay,
        hasData,
    })
);
